import React, { useEffect, useRef } from 'react';
import { flameSprite, flameSoftSprite, drawGasBlob } from '../../utils/emberPalette';

// Continuous Milky Way disc: gas and star dust orbiting on a flattened ellipse
// below the head, slowly drifting outward and respawning from the core.
// excited=true (during the intro) spins the disc faster and glows brighter.
const GalaxyDisc = ({ excited = false, anchorSel = '.xp-stage', centerY = 0.62 }) => {
  const canvasRef = useRef(null);
  const excitedRef = useRef(excited);
  excitedRef.current = excited;

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let w = 0;
    let h = 0;
    let cx = 0;
    let cy = 0;
    let maxR = 0;

    const layout = () => {
      w = canvas.offsetWidth;
      h = canvas.offsetHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const stage = document.querySelector(anchorSel);
      const cb = canvas.getBoundingClientRect();
      if (stage) {
        const sb = stage.getBoundingClientRect();
        cx = sb.left + sb.width / 2 - cb.left;
        cy = sb.top + sb.height * centerY - cb.top;
      } else {
        cx = w / 2;
        cy = h * 0.6;
      }
      maxR = Math.min(w * 0.46, 640);
    };
    layout();
    window.addEventListener('resize', layout);

    const isMobile = window.innerWidth <= 768;
    const G = isMobile ? 55 : 110;
    const D = isMobile ? 70 : 150;
    const SQUASH = 0.34;

    const spawn = (inner = false) => {
      const rad = inner ? 70 + Math.random() * 90 : 70 + Math.random() * (maxR - 70);
      return {
        th: Math.random() * Math.PI * 2,
        rad,
        drift: 0.1 + Math.random() * 0.22,
        size: 40 + Math.random() * 85,
        tw: Math.random() * Math.PI * 2,
        tws: 0.006 + Math.random() * 0.012,
      };
    };
    const gas = Array.from({ length: G }, () => spawn());
    const dust = Array.from({ length: D }, () => ({
      ...spawn(),
      size: 0.6 + Math.random() * 1.6,
    }));

    let raf;
    let boost = 0;
    const step = (p) => {
      const mul = 1 + boost * 1.4;
      p.th += 0.0042 * Math.sqrt(180 / p.rad) * mul;
      p.rad += p.drift * 0.14 * mul;
      p.tw += p.tws;
      if (p.rad > maxR) Object.assign(p, spawn(true));
    };
    const pos = (p) => ({
      x: cx + Math.cos(p.th) * p.rad,
      y: cy + Math.sin(p.th) * p.rad * SQUASH,
      front: (Math.sin(p.th) + 1) / 2,
      t: Math.min(p.rad / maxR, 1),
      edge: Math.min(1, (p.rad - 70) / 60) * Math.min(1, (maxR - p.rad) / 90),
    });

    const tick = () => {
      ctx.clearRect(0, 0, w, h);
      ctx.globalCompositeOperation = 'lighter';
      boost += ((excitedRef.current ? 1 : 0) - boost) * 0.03;
      const glowMul = 1 + boost * 0.7;
      // galactic core glow band under the head
      ctx.save();
      ctx.translate(cx, cy);
      ctx.scale(1, SQUASH);
      const core = ctx.createRadialGradient(0, 0, 0, 0, 0, maxR * 0.55);
      core.addColorStop(0, `rgba(255, 190, 120, ${0.16 * glowMul})`);
      core.addColorStop(0.45, `rgba(200, 120, 230, ${0.08 * glowMul})`);
      core.addColorStop(1, 'rgba(110, 140, 255, 0)');
      ctx.beginPath();
      ctx.arc(0, 0, maxR * 0.55, 0, Math.PI * 2);
      ctx.fillStyle = core;
      ctx.fill();
      ctx.restore();
      for (const p of gas) {
        step(p);
        const q = pos(p);
        const a = (0.13 + Math.sin(p.tw) * 0.04 + q.front * 0.09) * Math.max(q.edge, 0) * glowMul;
        drawGasBlob(ctx, flameSoftSprite(1 - q.t), q.x, q.y, p.size * (0.7 + q.front * 0.4), p.th + Math.PI / 2, 2.7, Math.min(Math.max(a, 0), 0.5));
      }
      for (const p of dust) {
        step(p);
        const q = pos(p);
        const a = (0.26 + Math.sin(p.tw) * 0.12 + q.front * 0.18) * Math.max(q.edge, 0) * glowMul;
        const s = p.size * 4.5 * (0.7 + q.front * 0.5);
        ctx.globalAlpha = Math.max(a, 0);
        ctx.drawImage(flameSprite(1 - q.t), q.x - s / 2, q.y - s / 2, s, s);
        ctx.globalAlpha = 1;
      }
      ctx.globalCompositeOperation = 'source-over';
      raf = requestAnimationFrame(tick);
    };
    tick();

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', layout);
    };
  }, []);

  return <canvas ref={canvasRef} className="xp-galaxy" aria-hidden="true" data-testid="xp-galaxy-disc" />;
};

export default GalaxyDisc;
